import { Box, Button, Container, Grid, Typography, CircularProgress, TextField, InputAdornment, Chip, Stack } from "@mui/material"
import { TitleScreen, SubTitleScreen, PrimaryButton } from "../../../SystemDesign/styles"
import CardImg from "../../../SystemDesign/cardImg"
import theme from "../../../theme"
import { useSalonsListQuery, useServicesPublicSalonListQuery } from "../../../services/GLOWClient"
import defaultSalonImage from "../../../assets/salon2.jpeg"
import SearchIcon from "@mui/icons-material/Search"
import { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { buildMediaUrl } from "../../../utils/urlHelpers"
import { useTranslation } from 'react-i18next'
import { getLocalizedField } from "../../../utils/localizedFields"

const NosSalon = () => {
  // Fetch salons from the backend
  // Local search state with debounce (mirror client view behavior)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const getTranslatedField = (item, field) => {
    return getLocalizedField(item, field, i18n.language);
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((search || "").trim())
    }, 300)
    return () => clearTimeout(handle)
  }, [search])

  const { data: salons, error, isLoading } = useSalonsListQuery(
    debouncedSearch ? { search: debouncedSearch, language: i18n.language } : { language: i18n.language }
  )

  // Use salons data from the API
  const displaySalons = salons || []

  // Function to get the salon's primary photo or first photo
  const getSalonPhoto = (salon) => {
    const buildUrl = (candidate) => buildMediaUrl(candidate)

    if (!salon?.photos || !Array.isArray(salon.photos) || salon.photos.length === 0) {
      return defaultSalonImage
    }

    const first = salon.photos[0]
    if (typeof first === "string") {
      // filename stored, usually under /media/<file>
      const filename = first.startsWith("media/")
        ? `/${first}`
        : first.startsWith("/media/")
          ? first
          : `/media/${first}`
      return buildUrl(filename) || defaultSalonImage
    }

    if (typeof first === "object") {
      const primary = salon.photos.find((p) => p && p.is_primary) || first
      const urlField = primary?.url || primary?.image || primary?.photo || primary?.file || primary?.path || ""
      return buildUrl(urlField) || defaultSalonImage
    }

    return defaultSalonImage
  }

  // New component to display services for a salon
  const SalonServices = ({ salonId }) => {
    const { data: services, isLoading, isError } = useServicesPublicSalonListQuery(
      { salonId },
      { skip: !salonId }
    )

    if (isLoading) return <CircularProgress size={20} />
    if (isError || !services?.length) return null

    // Get unique service categories with translations
    const categories = [...new Set(services.map(service => {
      const categoryName = service.category_details?.name;
      return categoryName ? getTranslatedField(service.category_details, 'name') : null;
    }).filter(Boolean))]
  
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
          {t('landing_salons.services_label')}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
          {categories.slice(0, 3).map((category, index) => (
            <Chip 
              key={index} 
              label={category} 
              size="small" 
              sx={{ 
                fontSize: '10px',
                height: '20px',
                '& .MuiChip-label': { px: 0.75 },
                bgcolor: theme.colors.primary[100],
                color: theme.colors.primary[700]
              }} 
            />
          ))}
          {categories.length > 3 && (
            <Chip 
              label={`+${categories.length - 3}`} 
              size="small" 
              sx={{ 
                fontSize: '10px',
                height: '20px',
                '& .MuiChip-label': { px: 0.75 },
                bgcolor: theme.colors.grey[200],
                color: theme.colors.grey[700]
              }} 
            />
          )}
        </Stack>
      </Box>
    )
  }

  // Navigate to search salon page
  const handleViewAllSalons = () => {
  navigate("/search-salon")
  }

  // Show more / less handling
  const [showAll, setShowAll] = useState(false)
  const visibleSalons = useMemo(() => (showAll ? displaySalons : displaySalons.slice(0, 6)), [displaySalons, showAll])

  const handleViewSalon = (id) => {
    navigate(`/salon/${id}`)
  }

  // Update the salon card to include services
  const renderSalonCard = (salon) => (
    <CardImg
      img={getSalonPhoto(salon)}
      title={getTranslatedField(salon, 'name')}
      subtitle={
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666", mb: 1 }}>
            {salon.address}
          </Typography>
          <SalonServices salonId={salon.salon_id} />
        </Box>
      }
      btn={t('landing_salons.view_details')}
      onClick={() => handleViewSalon(salon.salon_id)}
    />
  )

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: theme.colors.primary[50],
        width: "100%",
      }}
      id="nosSalonSection"
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography sx={TitleScreen(theme.colors)}>{t('landing_salons.title')}</Typography>
          <Typography sx={SubTitleScreen(theme.colors)}>
            {t('landing_salons.subtitle')}
          </Typography>
          {/* Search input */}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('landing_salons.search_placeholder')}
              variant="outlined"
              size="medium"
              sx={{ width: { xs: "100%", sm: "420px" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Salons Grid */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress sx={{ color: theme.colors.secondary[500] }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", color: "error.main", my: 4 }}>
            <Typography>{t('landing_salons.load_error')}</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={4} justifyContent="center">
              {visibleSalons.map((salon) => (
                <Grid item key={salon.salon_id} xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      height: "100%",
                      "&:hover": { transform: "translateY(-5px)", transition: "transform 0.3s ease" },
                    }}
                  >
                    {renderSalonCard(salon)}
                  </Box>
                </Grid>
              ))}
            </Grid>
            {displaySalons.length > 6 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowAll((v) => !v)}
                  sx={{
                    borderRadius: "24px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    borderColor: theme.colors.secondary[300],
                    color: theme.colors.secondary[700],
                    "&:hover": {
                      borderColor: theme.colors.secondary[500],
                      backgroundColor: theme.colors.secondary[50],
                    },
                  }}
                >
                  {showAll ? t('landing_salons.show_less') : t('landing_salons.show_more')}
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Call to Action */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <Button
            variant="contained"
            onClick={handleViewAllSalons}
            sx={{
              ...PrimaryButton,
              backgroundColor: theme.colors.secondary[500],
              color: "#ffffff",
              fontSize: { xs: "0.9375rem", md: "1rem" },
              fontWeight: 700,
              padding: { xs: "14px 32px", md: "16px 36px" },
              borderRadius: "28px",
              textTransform: "none",
              boxShadow: "0 6px 20px rgba(226, 194, 156, 0.3)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: theme.colors.secondary[600],
                transform: "translateY(-3px)",
                boxShadow: "0 8px 25px rgba(226, 194, 156, 0.4)",
              },
            }}
          >
            {t('landing_salons.cta_find_near')}
          </Button>
        </Box>

        {/* Optional: Mini Map (commented out as placeholder) */}
        {/* 
        <Box sx={{ mt: 6, height: '300px', borderRadius: theme.borderRadius.lg, overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
            Carte interactive des salons (intégration Google Maps/Leaflet)
          </Typography>
        </Box>
        */}
      </Container>
    </Box>
  )
}

export default NosSalon
